
import { h, Fragment } from 'preact'

import { useSubmission } from './../hooks/useSubmission'
import { scrollTo } from './../utility/scrollTo'

import { ExpiryCounter } from './../components/ExpiryCounter'
import { FieldRadio, FieldCheckboxes } from './../components/FieldOptions'
import { FieldTextArea } from './../components/FieldTextArea'
import { SubmitButton } from './../components/SubmitButton'


export function Screens({ screens, currentScreen, session }) {
	
	const Component = screens[currentScreen]?.component || null

	const beforeSubmit = data => {
		const map = screens?.[currentScreen]?.beforeSubmit || (x => x)
		return map(data)
	}

	const onSuccess = data => {
		screens?.[currentScreen]?.afterSubmit(data)
		resetSubmit()
	}

	const onError = data => {
		const el = document.querySelector('[class*="error"]')
		el && scrollTo(el, -80)
	}
	
	const { 
		fields, expires, validation, processing, submitted, 
		onSubmit, onInput, onInputDebounced, extendSession, resetSubmit 
	} = useSubmission({ session, beforeSubmit, onSuccess, onError })

	return <>
		<ExpiryCounter expires={expires} onExtend={extendSession} />
		<Component 
			fields={fields} 
			validation={validation} 
			processing={processing} 
			submitted={submitted} 
			onSubmit={onSubmit} 
			onInput={onInput} 
			session={session}
		/>
	</>
}


export function requireAdjustmentScreens(goTo, goToUrl, session, applyUrl) {
	return {
		'RequireAdjustments1': {
			component: RequireAdjustments1,
			afterSubmit: data => {
				if (data.RequireAdjustments.includes('Yes')) return goTo('RequireAdjustments2')
				if (session.eoLinkExternal) return goTo('EqualOppsExternal', applyUrl + 'equal-opportunities/')
				goTo('EqualOpps1', applyUrl + 'equal-opportunities/')
			},
		},
		'RequireAdjustments2': {
			component: RequireAdjustments2,
			afterSubmit: data => {
				if (session.eoLinkExternal) return goTo('EqualOppsExternal', applyUrl + 'equal-opportunities/')
				goTo('EqualOpps1', applyUrl + 'equal-opportunities/')
			}
		}
	}
}

export function eoScreens(goTo, goToUrl, reviewUrl) {
	return {
		'EqualOppsExternal': {
			component: EqualOppsExternal,
			beforeSubmit: data => ({ ...data, Stage: 'review' }),
			afterSubmit: data => goToUrl(reviewUrl)
		},
		'EqualOpps1': {
			component: EqualOpps1,
			afterSubmit: data => goTo('EqualOpps2')
		},
		'EqualOpps2': {
			component: EqualOpps2, 
			afterSubmit: data => goTo('EqualOpps3')
		},
		'EqualOpps3': {
			component: EqualOpps3,
			afterSubmit: data => goTo('EqualOpps4')
		},
		'EqualOpps4': {
			component: EqualOpps4,
			afterSubmit: data => goTo('EqualOpps5')
		},
		'EqualOpps5': {
			component: EqualOpps5,
			afterSubmit: data => goTo('EqualOpps6')
		},
		'EqualOpps6': {
			component: EqualOpps6,
			afterSubmit: data => goTo('EqualOpps7')
		},
		'EqualOpps7': {
			component: EqualOpps7,
			afterSubmit: data => goTo('EqualOpps8')
		},
		'EqualOpps8': {
			component: EqualOpps8,
			afterSubmit: data => {
				if (data.EOEthnicity.includes('White')) goTo('EqualOpps9')
				if (data.EOEthnicity.includes('Mixed')) goTo('EqualOpps10')
				if (data.EOEthnicity.includes('Asian')) goTo('EqualOpps11')
				if (data.EOEthnicity.includes('Black')) goTo('EqualOpps12')
				if (data.EOEthnicity.includes('Other')) goTo('EqualOpps13')
				if (data.EOEthnicity.includes('Prefer not to say')) goTo('EqualOpps14')
			}
		},
		'EqualOpps9': {
			component: EqualOpps9,
			afterSubmit: data => goTo('EqualOpps14')
		},
		'EqualOpps10': {
			component: EqualOpps10,
			afterSubmit: data => goTo('EqualOpps14')
		},
		'EqualOpps11': {
			component: EqualOpps11,
			afterSubmit: data => goTo('EqualOpps14')
		},
		'EqualOpps12': {
			component: EqualOpps12,
			afterSubmit: data => goTo('EqualOpps14')
		},
		'EqualOpps13': {
			component: EqualOpps13,
			afterSubmit: data => goTo('EqualOpps14')
		},
		'EqualOpps14': {
			component: EqualOpps14,
			afterSubmit: data => goTo('EqualOpps15')
		},
		'EqualOpps15': {
			component: EqualOpps15,
			beforeSubmit: data => {
				if (!data.EOHealthConditions.includes('Yes')) return { ...data, Stage: 'review' }
				return data
			},
			afterSubmit: data => {
				if (data.EOHealthConditions.includes('Yes')) goTo('EqualOpps16')
				if (!data.EOHealthConditions.includes('Yes')) goToUrl(reviewUrl)
			}
		},
		'EqualOpps16': {
			component: EqualOpps16,
			afterSubmit: data => goTo('EqualOpps17'),
		},
		'EqualOpps17': {
			component: EqualOpps17,
			beforeSubmit: data => ({ ...data, Stage: 'review' }),
			afterSubmit: data => goToUrl(reviewUrl)
		}
	}
}

export function RequireAdjustments1({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">Do you require additional support during the recruitment&nbsp;process?</h3>
		<p>For example, if you have a physical, mental or other health condition or illness which requires an adjustment to the recruitment process</p>
		<FieldRadio 
			name="RequireAdjustments" 
			onChange={onInput}
			value={fields['RequireAdjustments']}
			error={validation['RequireAdjustments']} 
			options={[
				'Yes', 
				'No',
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function RequireAdjustments2({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">Please provide any details of adjustments you require</h3>
		<FieldTextArea 
			rows="6"
			label="Adjustments" 
			name="RequireAdjustmentsComment" 
			placeholder="Enter your comment…"
			onBlur={onInput}
			value={fields['RequireAdjustmentsComment']} 
			error={validation['RequireAdjustmentsComment']} 
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOppsExternal({ processing, submitted, onSubmit, session }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">

		<h3 class="heading-4">Equality monitoring</h3> 

		<p>As part of the application process, we ask all candidates to complete an equal opportunities monitoring form by clicking on the button below.</p>

		<a target="_blank" class="button button--fill button--grey external-link" href={session.eoLinkExternal}>Equality monitoring form</a>

		<p>These questions help us {session.companyName && `and ${session.companyName}`} monitor diversity and ensure that no discrimination occurs in the recruitment process. Your answers are anonymised and only used to report on statistical trends.</p>
		<p>
			All information is treated as strictly confidential, and will be held in accordance with the Data Protection Act 2018. 
			{session.companyName && `
				Your answers are anonymised and are not treated as part of your application; only statistical data will be shared with ${session.companyName}.
			` || `
				Your answers are anonymised and are not treated as part of your application.
			`
			}
		</p>
		<details>
		    <summary>Why we ask equality questions</summary>
		    <p>
		    	Anderson Quigley believe that there should be no discrimination because of age, disability, gender reassignment, marriage and civil partnership, pregnancy and maternity, race, religion or belief, sex, sexual orientation. We operate an equal opportunity policy and commit to following all obligations under the Equality Act. We know we have an important role to play in promoting equality, diversity and inclusion and we take this responsibility seriously. We take appropriate steps to ensure that all applicants are reviewed on the basis of ability and the requirements of the promoted role.
		    </p>
		</details>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps1({ processing, submitted, onSubmit, session }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">


		<h3 class="heading-4">We’d like to ask you some equality questions</h3> 
		<p>These questions help us {session.companyName && `and ${session.companyName}`} monitor diversity and ensure that no discrimination occurs in the recruitment process. Your answers are anonymised and only used to report on statistical trends.</p>
		<p>
			All information is treated as strictly confidential, and will be held in accordance with the Data Protection Act 2018. 
			{session.companyName && `
				Your answers are anonymised and are not treated as part of your application; only statistical data will be shared with ${session.companyName}.
			` || `
				Your answers are anonymised and are not treated as part of your application.
			`
			}
		</p>
		<details>
		    <summary>Why we ask equality questions</summary>
		    <p>
		    	Anderson Quigley believe that there should be no discrimination because of age, disability, gender reassignment, marriage and civil partnership, pregnancy and maternity, race, religion or belief, sex, sexual orientation. We operate an equal opportunity policy and commit to following all obligations under the Equality Act. We know we have an important role to play in promoting equality, diversity and inclusion and we take this responsibility seriously. We take appropriate steps to ensure that all applicants are reviewed on the basis of ability and the requirements of the promoted role.
		    </p>
		</details>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps2({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">What is your age?</h3> 
		<FieldRadio 
			name="EOAge" 
			onChange={onInput}
			value={fields['EOAge']}
			error={validation['EOAge']} 
			options={[
				'16–24', '25–29', 
				'30–34', '35–39', 
				'40–44', '45–49', 
				'50–54', '55–59', 
				'60–64', '65+', 
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps3({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">What is your marital status?</h3> 

		{/* 
			Not up to date with the government data harmonised standard
			https://gss.civilservice.gov.uk/policy-store/marital-or-partnership-status/ 
			
			Q: What is your legal marital or registered civil partnership status?
			Never married and never registered in a civil partnership.
			Married.
			In a registered civil partnership.
			Separated, but still legally married.
			Separated, but still legally in a civil partnership.
			Divorced.
			Formerly in a civil partnership which is now legally dissolved.
			Widowed.
			Surviving partner from a registered civil partnership.
		*/}

		<FieldRadio 
			name="EOMaritalStatus" 
			onChange={onInput}
			value={fields['EOMaritalStatus']}
			error={validation['EOMaritalStatus']} 
			options={[
				'Married', 
				'Civil partnership', 
				'Divorced', 
				'Single', 
				'Co-habiting', 
				'Separated', 
				'Widowed', 
				{
					value: 'Other', 
					input: true,
					inputName: 'EOMaritalStatusOther', 
					inputText: "How would you describe your marital status? (optional)",
					inputValue: fields['EOMaritalStatusOther']
				}
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps4({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">What is your sex?</h3> 
		{/* 
			Generally 'sex', not 'gender' is asked first, and only after 
			asking if your gender identity is the same as your sex registered at birth:
			https://gss.civilservice.gov.uk/policy-store/sex-and-gender/
		*/}
		<FieldRadio 
			name="EOSex" 
			onChange={onInput}
			value={fields['EOSex']}
			error={validation['EOSex']} 
			options={[
				'Female', 
				'Male',
				{
					value: 'Other', 
					input: true,
					inputName: 'EOSexOther', 
					inputText: "How would you describe your sex? (optional)",
					inputValue: fields['EOSexOther']
				}
			]} 
			or="Prefer not to say" 
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps5({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		{/* 
			Gnder options are not specified, only allowing a free text field:
			https://analysisfunction.civilservice.gov.uk/policy-store/gender-identity/
		*/}
		<h3 class="heading-4">Is the gender you identify with the same as your sex registered at birth?</h3>
		<FieldRadio 
			name="EOGenderAtBirth" 
			onChange={onInput}
			value={fields['EOGenderAtBirth']}
			error={validation['EOGenderAtBirth']} 
			options={[
				'Yes', 
				{
					value: 'No', 
					input: true,
					inputName: 'EOGenderAtBirthNo', 
					inputText: "What is your gender identity? (optional)",
					inputValue: fields['EOGenderAtBirthNo']
				}
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps6({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">Which of the following best describes your sexual orientation?</h3>
		<FieldRadio 
			name="EOSexualOrientation" 
			onChange={onInput}
			value={fields['EOSexualOrientation']}
			error={validation['EOSexualOrientation']} 
			options={[
				'Heterosexual or straight', 
				'Gay or lesbian', 
				'Bisexual', 
				{
					value: 'Other', 
					input: true,
					inputName: 'EOSexualOrientationOther', 
					inputText: "How would you describe your sexual orientation? (optional)",
					inputValue: fields['EOSexualOrientationOther']
				}
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps7({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">How would you describe your national identity?</h3>
		{/*
			Current 'nationality' text field not up to date with the government data harmonised standard
			https://gss.civilservice.gov.uk/policy-store/national-identity-harmonised-standard/
			
			Q: How would you describe your national identity?
			English
			Welsh
			Scottish
			Northern Irish
			British
			Irish
			Other, please describe
		*/}
		<p>(Check all that apply)</p>
		<FieldCheckboxes 
			name="EONationality"
			onChange={onInput}
			values={fields['EONationality']}
			error={validation['EONationality']} 
			options={[
				'English',
				'Welsh',
				'Scottish',
				'Northern Irish',
				'British',
				'Irish',
				{
					value: 'Other', 
					input: true,
					inputName: 'EONationalityOther', 
					inputText: "What is your nationality? (optional)",
					inputValue: fields['EONationalityOther']
				},
			]} 
			or="Prefer not to say"
		/>	
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps8({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		{/*
			New update from 2021 Census:
			https://www.ons.gov.uk/census/censustransformationprogramme/questiondevelopment/nationalidentityethnicgrouplanguageandreligionquestiondevelopmentforcensus2021#questions-recommended-for-census-2021
			Recommended from: https://gss.civilservice.gov.uk/policy-store/ethnicity-harmonised-standard/#questions-for-england
			GDS guidance here: https://design-system.service.gov.uk/patterns/equality-information/
		*/}
		<h3 class="heading-4">Which option best describes your ethnic group or background?</h3>
		<p>(The following categories have been devised by the UK Government and are recommended by the Equality and Human Rights Commission)</p>
		<FieldRadio 
			name="EOEthnicity"
			onChange={onInput}
			value={fields['EOEthnicity']}
			error={validation['EOEthnicity']} 
			options={[
				'White',
				'Mixed or multiple ethnic groups',
				'Asian or Asian British',
				'Black, African, Caribbean or Black British',
				'Other ethnic group',
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps9({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">Which of the following best describes your White background?</h3>
		<FieldRadio 
			name="EOEthnicityWhite"
			onChange={onInput}
			value={fields['EOEthnicityWhite']}
			error={validation['EOEthnicityWhite']} 
			options={[
				'English, Welsh, Scottish, Northern Irish or British', 
				'Irish', 
				'Gypsy or Irish Traveller', 
				'Roma', 
				{
					value: 'Any other White background', 
					input: true,
					inputName: 'EOEthnicityWhiteOther', 
					inputText: "How would you describe your background? (optional)",
					inputValue: fields['EOEthnicityWhiteOther']
				}
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps10({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">Which of the following best describes your Mixed or Multiple ethnic groups background?</h3>
		<FieldRadio 
			name="EOEthnicityMixed"
			onChange={onInput}
			value={fields['EOEthnicityMixed']}
			error={validation['EOEthnicityMixed']} 
			options={[
				'White and Black Caribbean', 
				'White and Black African', 
				'White and Asian', 
				{
					value: 'Any other Mixed or Multiple ethnic background', 
					input: true,
					inputName: 'EOEthnicityMixedOther', 
					inputText: "How would you describe your background? (optional)",
					inputValue: fields['EOEthnicityMixedOther']
				}
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps11({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">Which of the following best describes your Asian or Asian British background?</h3>
		<FieldRadio 
			name="EOEthnicityAsian"
			onChange={onInput}
			value={fields['EOEthnicityAsian']}
			error={validation['EOEthnicityAsian']} 
			options={[
				'Indian', 
				'Pakistani', 
				'Bangladeshi', 
				'Chinese',
				{
					value: 'Any other Asian background', 
					input: true,
					inputName: 'EOEthnicityAsianOther', 
					inputText: "How would you describe your background? (optional)",
					inputValue: fields['EOEthnicityAsianOther']
				}
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps12({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">Which of the following best describes your Black, African, Caribbean or Black British background?</h3>
		<FieldRadio 
			name="EOEthnicityBlack"
			onChange={onInput}
			value={fields['EOEthnicityBlack']}
			error={validation['EOEthnicityBlack']} 
			options={[
				'African', 
				'Caribbean', 
				{
					value: 'Any other Black, African or Caribbean background', 
					input: true,
					inputName: 'EOEthnicityBlackOther', 
					inputText: "How would you describe your background? (optional)",
					inputValue: fields['EOEthnicityBlackOther']
				}
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps13({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		<h3 class="heading-4">Which of the following best describes your background?</h3>
		<FieldRadio 
			name="EOEthnicityOther"
			onChange={onInput}
			value={fields['EOEthnicityOther']}
			error={validation['EOEthnicityOther']} 
			options={[
				'Arab', 
				{
					value: 'Any other ethnic group', 
					input: true,
					inputName: 'EOEthnicityOtherOther', 
					inputText: "How would you describe your background? (optional)",
					inputValue: fields['EOEthnicityOtherOther']
				}
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps14({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		{/*
			Largely in line with harmonisation — just added christian description, and removed 'or no belief' + reorder
		*/}
		<h3 class="heading-4">What is your religion?</h3>
		<FieldRadio 
			name="EOReligion"
			onChange={onInput}
			value={fields['EOReligion']}
			error={validation['EOReligion']} 
			options={[
				'No religion',
				{value: 'Christian', description: 'Including Church of England, Catholic, Protestant and all other Christian denominations.'},
				'Buddhist',
				'Hindu',
				'Jewish',
				'Muslim',
				'Sikh',
				{
					value: 'Any other religion', 
					input: true,
					inputName: 'EOReligionOther',
					inputText: "What is your religion? (optional)",
					inputValue: fields['EOReligionOther']
				},
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps15({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		{/*
			https://gss.civilservice.gov.uk/policy-store/long-lasting-health-conditions-and-illness/
		*/}
		<h3 class="heading-4">Do you have any physical or mental health conditions or illnesses?</h3>
		<FieldRadio 
			name="EOHealthConditions"
			onChange={onInput}
			value={fields['EOHealthConditions']}
			error={validation['EOHealthConditions']} 
			options={[
				'Yes',
				'No',
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps16({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		{/*
			https://gss.civilservice.gov.uk/policy-store/activity-restriction/
		*/}
		<h3 class="heading-4">Do any of your conditions or illnesses reduce your ability to carry out day-to-day activities?</h3>
		<p>(For example eating, washing, walking or going shopping)</p>
		<FieldRadio 
			name="EOHealthConditionsEffectActivities"
			onChange={onInput}
			value={fields['EOHealthConditionsEffectActivities']}
			error={validation['EOHealthConditionsEffectActivities']} 
			options={[
				'Yes, a lot',
				'Yes, a little',
				'Not at all',
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}

export function EqualOpps17({ fields, validation, processing, submitted, onSubmit, onInput }) {
	return <form onSubmit={onSubmit} class="text-main text-fade">
		{/* 
			https://gss.civilservice.gov.uk/policy-store/impairment/
		*/}
		<h3 class="heading-4">Do any of your conditions or illnesses affect you in any of the following areas?</h3>
		<FieldCheckboxes 
			name="EOConditions"
			onChange={onInput}
			values={fields['EOConditions']}
			error={validation['EOConditions']} 
			options={[
				{value: 'Vision', description: '(for example blindness or partial sight)'},
				{value: 'Hearing', description: '(for example deafness or partial hearing)'},
				{value: 'Mobility', description: '(for example walking short distances or climbing stairs)'},
				{value: 'Dexterity', description: '(for example lifting and carrying objects, using a keyboard)'},
				{value: 'Learning or understanding or concentrating'},
				{value: 'Memory'},
				{value: 'Mental health'},
				{value: 'Stamina or breathing or fatigue'},
				{value: 'Socially or behaviourally', description: '(for example associated with autism spectrum disorder (ASD) which includes Asperger’s, or attention deficit hyperactivity disorder (ADHD))'},
				{
					value: 'Other', 
					input: true, 
					inputName: 'EOConditionsOther', 
					inputText: 'Describe your health condition (optional)',
					inputValue: fields['EOConditionsOther']
				},
				{value: 'None of the above'},
			]} 
			or="Prefer not to say"
		/>
		<SubmitButton class="button button--fill button--yellow" processing={processing || submitted}>Continue</SubmitButton>
	</form>
}
